import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Input } from "../ui/input";
import { agencyDisplayName } from "../../utils/agencyScope";
import {
  INTERNAL_AGENCY_FILTER,
  USER_STATUS_OPTIONS,
  userFiltersDefaultState,
} from "../../utils/constants/Roles";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import FilterSelect from "../common/FilterSelect";

const FILTER_KEYS = Object.keys(userFiltersDefaultState);

/** Search plus Status, Role and Agency filters for the Users tab, mirrored into the URL. */
const UserFilterBar = ({ roles, agencies, showAgency, onChange }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState(() =>
    Object.fromEntries(
      FILTER_KEYS.map((key) => [key, searchParams.get(key) || ""])
    )
  );
  const search = useDebouncedValue(filters.search.trim(), 400);
  const { status, role, agency } = filters;
  const isInternal = agency === INTERNAL_AGENCY_FILTER;
  const roleOptions = roles.map((r) => ({
    value: String(r.id),
    label: r.name,
  }));
  const agencyOptions = [
    { value: INTERNAL_AGENCY_FILTER, label: "Internal (Seekho)" },
    ...agencies.map((a, i) => ({
      value: String(a.id),
      label: agencyDisplayName(a, i),
    })),
  ];
  const apiParams = {
    search: search || undefined,
    status: status || undefined,
    role_id: role || undefined,
    internal: isInternal || undefined,
    agency_id: !isInternal && agency ? agency : undefined,
  };
  const filterKey = JSON.stringify(apiParams);
  const hasFilters = Object.values(filters).some(Boolean);

  useEffect(() => {
    onChange({ apiParams: JSON.parse(filterKey), key: filterKey });
  }, [filterKey, onChange]);

  useEffect(() => {
    const applied = { search, status, role, agency };
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        FILTER_KEYS.forEach((key) =>
          applied[key] ? next.set(key, applied[key]) : next.delete(key)
        );
        return next;
      },
      { replace: true }
    );
  }, [search, status, role, agency, setSearchParams]);

  const handleFilter = ({ key, value }) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  return (
    <div className="flex flex-wrap items-center gap-2 mb-4">
      <Input
        placeholder="Search name, email or username…"
        value={filters.search}
        onChange={(e) => handleFilter({ key: "search", value: e.target.value })}
        className="min-w-[220px] flex-1 max-w-[360px] h-9 bg-white border-gray-300"
      />
      <FilterSelect
        value={filters.status}
        onChange={(value) => handleFilter({ key: "status", value })}
        options={USER_STATUS_OPTIONS}
        allLabel="All statuses"
        placeholder="Status"
        width="w-[150px]"
      />
      <FilterSelect
        value={filters.role}
        onChange={(value) => handleFilter({ key: "role", value })}
        options={roleOptions}
        allLabel="All roles"
        placeholder="Role"
        width="w-[180px]"
      />
      {showAgency && (
        <FilterSelect
          value={filters.agency}
          onChange={(value) => handleFilter({ key: "agency", value })}
          options={agencyOptions}
          allLabel="All agencies"
          placeholder="Agency"
          width="w-[200px]"
        />
      )}
      {hasFilters && (
        <button
          type="button"
          onClick={() => setFilters(userFiltersDefaultState)}
          className="text-sm text-[#7f7ad5] hover:text-[#6b66c4] font-medium px-2"
        >
          Clear all
        </button>
      )}
    </div>
  );
};

export default UserFilterBar;
